import type { I18nKey } from '@brao/shared';
import { post, request } from '../api';
import type { TaskBusiness, TaskMember, TaskPriority, TaskStatus } from './tasks';

export interface TaskTemplate {
  kind: 'task_template';
  title: string;
  description?: string | null;
  priority?: TaskPriority;
  assigneeId?: string | null;
  projectId?: string | null;
}

export interface ProcessRun {
  id: string;
  key: string;
  title: string;
  status: TaskStatus;
  priority: TaskPriority;
  scheduledAt: string;
  createdAt: string;
  closedAt: string | null;
  overdue: boolean;
  manual: boolean;
}

export interface ProcessRow {
  id: string;
  businessId: string;
  business: { id: string; name: string; taskPrefix: string };
  name: string;
  description: string | null;
  schedule: string | null;
  timezone: string;
  scheduleText: { key: I18nKey; params: Record<string, string> };
  slaMinutes: number | null;
  enabled: boolean;
  ownerId: string | null;
  owner: { id: string; displayName: string; type: string } | null;
  lastRunAt: string | null;
  nextRunAt: string | null;
  taskTemplate: TaskTemplate | null;
  lastRun: ProcessRun | null;
  createdAt: string;
  updatedAt: string;
}

export interface TaskTemplateInput {
  title: string;
  description?: string | null;
  priority?: TaskPriority;
  assigneeId?: string | null;
  projectId?: string | null;
}

export interface CreateProcessInput {
  businessId: string;
  name: string;
  description?: string | null;
  schedule: string;
  timezone?: string;
  slaMinutes?: number | null;
  ownerId?: string | null;
  taskTemplate: TaskTemplateInput;
}

export type UpdateProcessInput = Partial<Omit<CreateProcessInput, 'businessId'>>;

export interface ProcessProject { id: string; name: string; status: string }

const q = (businessId?: string) => (businessId ? `?businessId=${encodeURIComponent(businessId)}` : '');

export const processesApi = {
  list: (businessId?: string) => request<ProcessRow[]>(`/processes${q(businessId)}`),
  get: (id: string) => request<ProcessRow>(`/processes/${encodeURIComponent(id)}`),
  runs: (id: string) => request<ProcessRun[]>(`/processes/${encodeURIComponent(id)}/runs`),
  create: (input: CreateProcessInput) => post<ProcessRow>('/processes', input),
  update: (id: string, input: UpdateProcessInput) => request<ProcessRow>(`/processes/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(input) }),
  enable: (id: string) => post<ProcessRow>(`/processes/${encodeURIComponent(id)}/enable`),
  disable: (id: string) => post<ProcessRow>(`/processes/${encodeURIComponent(id)}/disable`),
  run: (id: string) => post<{ jobId: string; slot: string }>(`/processes/${encodeURIComponent(id)}/run`),
  businesses: () => request<TaskBusiness[]>('/processes/businesses'),
  members: (businessId?: string) => request<TaskMember[]>(`/processes/members${q(businessId)}`),
  projects: (businessId: string) => request<ProcessProject[]>(`/processes/projects${q(businessId)}`),
};
